import PageHeader from "@/components/PageHeader";
import Spinner from "@/components/Spinner";
import { Card, CardContent } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { Module, PATH_ROOT } from "@/constants";
import { formatDateTime } from "@/lib/utils/formatters";
import { useModulePermissions } from "@/modules/account/hooks/useModulePermissions";
import { useAppSettingsQuery } from "@/modules/app-settings/hooks/useAppSettingsQuery";
import { useUpdateAppSettingMutation } from "@/modules/app-settings/hooks/useUpdateAppSettingMutation";
import { ReadAppSetting } from "@/types/appSettings";
import { useTranslation } from "react-i18next";

const AppSettingRow = ({
  setting,
  canUpdate,
  isSaving,
  onChange,
}: {
  setting: ReadAppSetting;
  canUpdate: boolean;
  isSaving: boolean;
  onChange: (value: boolean) => void;
}) => {
  const { t } = useTranslation();
  const switchId = `app-setting-${setting.code}`;

  return (
    <div className="flex items-center justify-between gap-6 py-4">
      <div className="space-y-1">
        <label htmlFor={switchId} className="text-sm font-medium">
          {setting.name}
        </label>
        {setting.description && (
          <p className="text-sm text-muted-foreground">{setting.description}</p>
        )}
        {setting.updated_by_name && (
          <p className="text-xs text-muted-foreground">
            {t("appSettings.lastChange", {
              date: formatDateTime(setting.updated_at),
              user: setting.updated_by_name,
            })}
          </p>
        )}
      </div>
      {setting.value_type === "boolean" && (
        <Switch
          id={switchId}
          checked={setting.value === true}
          disabled={!canUpdate || isSaving}
          onCheckedChange={onChange}
        />
      )}
    </div>
  );
};

export const AppSettingsDirectory = () => {
  const { t } = useTranslation();
  const { can_update } = useModulePermissions(Module.AppSettings);
  const query = useAppSettingsQuery();
  const updateMutation = useUpdateAppSettingMutation();
  const breadcrumbs = [
    { label: t("breadcrumb.home"), href: PATH_ROOT },
    { label: t("navigation.appSettings") },
  ];

  if (query.isLoading || query.isPending) {
    return (
      <>
        <PageHeader breadcrumbItems={breadcrumbs} />
        <Spinner label={t("table.loading")} className="h-full w-full" />
      </>
    );
  }

  const settings = query.data?.items ?? [];

  return (
    <>
      <PageHeader breadcrumbItems={breadcrumbs} />
      <div className="p-4">
        <Card>
          <CardContent className="divide-y px-6 py-1">
            {settings.length === 0 && (
              <p className="py-4 text-sm text-muted-foreground">
                {t("appSettings.empty")}
              </p>
            )}
            {settings.map((setting) => (
              <AppSettingRow
                key={setting.code}
                setting={setting}
                canUpdate={can_update}
                isSaving={updateMutation.isPending}
                onChange={(value) =>
                  updateMutation.mutate({ code: setting.code, value })
                }
              />
            ))}
          </CardContent>
        </Card>
      </div>
    </>
  );
};
